'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { H5 } from 'foundations-components/transitional/components/H5';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Select } from 'foundations-components/transitional/components/Select';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TableBorderStyles, TableDimensionTypes } from '../../constants/table';
import { textButtonStyles, toolbarI18nKey } from '../../constants/toolbar';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange';
import { isPercentage, pixelStringToNumber } from '../../utils/table';
import { PixelStepperInput } from '../steppers';
import StyleProtectedUIDropdown from '../style-protected/StyleProtectedUIDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const extractEventValue = (fn, key = 'value') => evt => {
  const {
    [key]: value
  } = evt.target;
  fn(value);
};
const propTypes = {
  className: PropTypes.string.isRequired,
  dropdownClassName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  tableWidth: PropTypes.string.isRequired,
  borderWidth: PropTypes.number.isRequired,
  borderStyle: PropTypes.oneOf(TableBorderStyles).isRequired,
  cellPadding: PropTypes.number.isRequired,
  tableCaption: PropTypes.bool.isRequired,
  tableIsResponsive: PropTypes.bool.isRequired,
  tableStyle: PropTypes.string.isRequired,
  onBorderWidthChange: PropTypes.func.isRequired,
  onBorderStyleChange: PropTypes.func.isRequired,
  onTableDimensionTypeChange: PropTypes.func.isRequired,
  onCellPaddingChange: PropTypes.func.isRequired,
  onTableCaptionChange: PropTypes.func.isRequired,
  onTableResponsiveChange: PropTypes.func.isRequired,
  onTableCustomStyleChange: PropTypes.func.isRequired
};
function TableStyleControlButton(props) {
  const {
    className,
    dropdownClassName,
    disabled,
    tableWidth,
    borderWidth,
    onBorderWidthChange,
    borderStyle,
    onBorderStyleChange,
    onTableDimensionTypeChange,
    cellPadding,
    onCellPaddingChange,
    tableCaption,
    onTableCaptionChange,
    tableIsResponsive,
    onTableResponsiveChange,
    tableStyle: _tableStyle,
    onTableCustomStyleChange
  } = props;
  const [handleBorderWidthChange, handleBorderWidthStep] = useDebouncedOnChange(extractEventValue(onBorderWidthChange));
  const [handleCellPaddingChange, handleCellPaddingStep] = useDebouncedOnChange(extractEventValue(onCellPaddingChange));
  const [tableStyle, setTableStyle] = useState(_tableStyle);
  const [onTableCssChange] = useDebouncedOnChange(onTableCustomStyleChange);
  const handleTableCustomCssChange = evt => {
    const {
      value
    } = evt.target;
    setTableStyle(value);
    onTableCssChange(value);
  };
  useEffect(() => {
    setTableStyle(_tableStyle);
  }, [_tableStyle]);
  return /*#__PURE__*/_jsx(StyleProtectedUIDropdown, {
    "data-test-id": "table-style-control-button",
    use: "unstyled",
    buttonText: I18n.text(toolbarI18nKey('tableStyle')),
    buttonUse: "transparent",
    buttonClassName: className,
    autoPlacement: "horiz",
    placement: "bottom right",
    closeOnMenuClick: false,
    dropdownClassName: dropdownClassName,
    animateOnToggle: false,
    disabled: disabled,
    arrowSize: "medium",
    children: /*#__PURE__*/_jsxs(Fieldset, {
      "data-test-id": "table-style-control-content",
      margin: "400",
      minInlineSize: "280px",
      maxInlineSize: "340px",
      _size: "small",
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "RichTextLib.table.toolbar.table.title"
        })
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.borderWidth"
          })
        }), /*#__PURE__*/_jsx(PixelStepperInput, {
          "data-test-id": "table-border-width-input",
          min: 0,
          max: 30,
          inlineSize: "144px",
          value: pixelStringToNumber(borderWidth),
          onChange: handleBorderWidthChange,
          onStep: handleBorderWidthStep
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.borderStyle"
          })
        }), /*#__PURE__*/_jsx(Select, {
          "data-test-id": "table-border-style-select",
          inlineSize: "144px",
          options: TableBorderStyles.map(style => ({
            value: style,
            text: I18n.text(`RichTextLib.table.toolbar.borderStyleOptions.${style}`)
          })),
          value: borderStyle,
          onChange: extractEventValue(onBorderStyleChange)
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.table.widthType"
          })
        }), /*#__PURE__*/_jsx(Select, {
          "data-test-id": "table-dimension-type-select",
          inlineSize: "144px",
          options: Object.values(TableDimensionTypes).map(type => ({
            value: type,
            text: I18n.text(`RichTextLib.table.toolbar.table.${type}`)
          })),
          value: isPercentage(tableWidth) ? TableDimensionTypes.PERCENTAGE : TableDimensionTypes.PIXELS,
          onChange: extractEventValue(onTableDimensionTypeChange)
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.table.cellPadding"
          })
        }), /*#__PURE__*/_jsx(PixelStepperInput, {
          "data-test-id": "table-cell-padding-input",
          min: 0,
          max: 40,
          inlineSize: "144px",
          value: pixelStringToNumber(cellPadding),
          onChange: handleCellPaddingChange,
          onStep: handleCellPaddingStep
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(HelpIcon, {
          title: I18n.text('RichTextLib.table.toolbar.table.captionTooltip'),
          typography: "heading-50",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.table.caption"
          })
        }), /*#__PURE__*/_jsx(Toggle, {
          "data-test-id": "table-caption-toggle",
          size: "xs",
          checked: tableCaption,
          onChange: extractEventValue(onTableCaptionChange, 'checked')
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(HelpIcon, {
          title: I18n.text('RichTextLib.table.toolbar.table.responsiveTooltip'),
          typography: "heading-50",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.table.responsive"
          })
        }), /*#__PURE__*/_jsx(Toggle, {
          "data-test-id": "table-responsive-toggle",
          size: "xs",
          checked: tableIsResponsive,
          onChange: extractEventValue(onTableResponsiveChange, 'checked')
        })]
      }), /*#__PURE__*/_jsx(FormControl, {
        marginBlockStart: "200",
        label: I18n.text('RichTextLib.table.toolbar.table.styleField'),
        tooltip: I18n.text('RichTextLib.table.toolbar.table.styleFieldTooltip'),
        children: /*#__PURE__*/_jsx(TextArea, {
          "data-test-id": "table-custom-css-input",
          blockSize: "82px",
          rows: 3,
          resize: false,
          value: tableStyle,
          onChange: handleTableCustomCssChange
        })
      })]
    })
  });
}
TableStyleControlButton.propTypes = propTypes;
TableStyleControlButton.displayName = 'TableStyleControlButton';
const StyledTableStyleControlButton = styled(TableStyleControlButton).withConfig({
  displayName: "TableStyleControlButton__StyledTableStyleControlButton",
  componentId: "sc-1hcjaas-0"
})(["", ""], textButtonStyles);
export default StyledTableStyleControlButton;